/**
 * LibraryAgentNew — create a new agent template in the library.
 *
 * Fields:
 *   - id (slug)      — unique identifier, auto-derived from name if empty
 *   - name           — display label (e.g. "Triage Oncology")
 *   - description    — one-line summary
 *   - specialties    — multi-tag (any number of specialties)
 *   - content        — prompt text
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as api from "../api.js";
import { PageHeader, ErrorBanner } from "../components.jsx";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function LibraryAgentNew() {
  const nav = useNavigate();

  const [form, setForm] = useState({
    id: "",
    name: "",
    description: "",
    specialties: [],
    customSpecialty: "",
    content: "",
  });
  const [existingSpecialties, setExistingSpecialties] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Discover specialties from the catalog + any agent tags already in
  // the library so the picker shows everything that's been used.
  useEffect(() => {
    (async () => {
      try {
        const [catRes, agentsRes] = await Promise.all([
          api.listSpecialtyCatalog().catch(() => ({ data: [] })),
          api.listAgents({ mode: "draft" }).catch(() => ({ data: [] })),
        ]);
        const seen = new Set();
        for (const s of catRes.data || []) seen.add(s._id);
        for (const a of agentsRes.data || []) {
          for (const t of a.specialties || []) seen.add(t);
        }
        setExistingSpecialties([...seen].sort());
      } catch {
        // ignore; manual custom tag entry still works
      }
    })();
  }, []);

  const effectiveId = slugify(form.id || form.name);

  const toggleSpecialty = (s) => {
    setForm((f) => ({
      ...f,
      specialties: f.specialties.includes(s)
        ? f.specialties.filter((x) => x !== s)
        : [...f.specialties, s],
    }));
  };

  const addCustomSpecialty = () => {
    const s = slugify(form.customSpecialty);
    if (!s) return;
    if (!form.specialties.includes(s)) {
      setForm((f) => ({
        ...f,
        specialties: [...f.specialties, s],
        customSpecialty: "",
      }));
      if (!existingSpecialties.includes(s)) {
        setExistingSpecialties((arr) => [...arr, s].sort());
      }
    } else {
      setForm((f) => ({ ...f, customSpecialty: "" }));
    }
  };

  const save = async () => {
    setError(null);
    if (!form.name.trim()) {
      setError(new Error("Name is required"));
      return;
    }
    if (!effectiveId) {
      setError(new Error("ID is required (provide an ID or a name)"));
      return;
    }
    if (form.specialties.length === 0) {
      setError(new Error("At least one specialty tag is required"));
      return;
    }
    if (!form.content.trim()) {
      setError(new Error("Content is required"));
      return;
    }

    setBusy(true);
    try {
      await api.writeAgent(effectiveId, {
        content: form.content,
        mode: "draft",
        name: form.name.trim(),
        description: form.description.trim(),
        specialties: form.specialties,
        author: "admin",
        commit_msg: `Create agent ${effectiveId}`,
      });
      nav(`/library/draft/agents/${encodeURIComponent(effectiveId)}`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="New Agent"
        subtitle="Create a reusable agent template. Tag it with one or more specialties so downstream sub-tenants can fork it."
        breadcrumbs={[
          { label: "Library", to: "/library/draft/platform" },
          { label: "Agents", to: "/library/draft/agents" },
          { label: "New" },
        ]}
        actions={
          <div className="btn-row">
            <button className="btn" onClick={() => nav(-1)} disabled={busy}>
              Cancel
            </button>
            <button className="btn primary" onClick={save} disabled={busy}>
              {busy ? "Saving..." : "Create draft"}
            </button>
          </div>
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        <div className="card">
          <div className="card-header">
            <h3>Identity</h3>
          </div>
          <div className="form-field">
            <label>Name</label>
            <input
              type="text"
              placeholder="e.g. Triage Oncology"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div className="form-field">
            <label>ID (slug)</label>
            <input
              type="text"
              placeholder={effectiveId || "auto-derived from name"}
              value={form.id}
              onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
            />
            <div className="help">
              {effectiveId ? (
                <>Will be saved as <span className="mono">{effectiveId}</span></>
              ) : (
                "Leave blank to auto-derive from name."
              )}
            </div>
          </div>
          <div className="form-field">
            <label>Description</label>
            <input
              type="text"
              placeholder="One-line summary"
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Agent groups</h3>
          </div>
          <p className="muted small mb-16">
            Tag this agent with every specialty it applies to. Existing tags
            come from the specialty catalog; you can also add custom ones.
          </p>
          <div className="btn-row mb-8" style={{ flexWrap: "wrap" }}>
            {existingSpecialties.length === 0 ? (
              <span className="subtle small">
                No existing specialties yet — add one below.
              </span>
            ) : (
              existingSpecialties.map((s) => {
                const on = form.specialties.includes(s);
                return (
                  <button
                    key={s}
                    className={`btn sm ${on ? "primary" : ""}`}
                    onClick={() => toggleSpecialty(s)}
                  >
                    {on ? "✓ " : ""}
                    {s}
                  </button>
                );
              })
            )}
          </div>
          <div className="btn-row">
            <input
              type="text"
              placeholder="Add custom specialty..."
              value={form.customSpecialty}
              onChange={(e) =>
                setForm((f) => ({ ...f, customSpecialty: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustomSpecialty();
                }
              }}
              style={{ flex: 1, padding: "6px 10px", fontSize: 13 }}
            />
            <button className="btn sm" onClick={addCustomSpecialty}>
              Add
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Content</h3>
          </div>
          <div className="form-field">
            <textarea
              placeholder="The agent prompt..."
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              spellCheck="false"
            />
            <div className="help">
              {form.content.length.toLocaleString()} chars
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
